import { getApiInstance } from './api.helpers';
import { substituteVariablesInString } from './step_response_variable.helpers';
/**
 * When a response value is an array of strings containing placeholders like {number}, {color},
 * substitutes them with the corresponding variable's display value from the variables object.
 * Uses shared substituteVariablesInString from step_response_variable.helpers.
 * Graceful fallback: non-array values, arrays that are not all strings, unknown placeholder
 * formats, and missing variable keys are left unchanged so the full data is still passed to the LLM.
 */
export function substituteVariablesInResponseValue(value, variables) {
    if (!Array.isArray(value) || !value.every((x) => typeof x === 'string')) {
        return value;
    }
    return value.map((s) => substituteVariablesInString(s, variables));
}
/**
 * Returns responses from the review bundle that are non-personal and have question_text and value
 * parsed (variable placeholders substituted). Safe to call with null/undefined bundle; returns [].
 */
export function prepareReviewBundle(bundle) {
    const responses = bundle?.responses;
    if (!responses?.length) {
        return [];
    }
    return responses.flatMap((response) => {
        if (response.personalData) {
            return [];
        }
        let processedValue = substituteVariablesInResponseValue(response.value, response.variables);
        if (Array.isArray(processedValue)) {
            processedValue = processedValue.join(', ');
        }
        return [
            {
                ...response,
                question_text: substituteVariablesInString(response.question_text, response.variables),
                value: processedValue,
            },
        ];
    });
}
const makeApiCall = async ({ method = 'get', path, payload, params, environment, webApi, onError, }) => {
    let response;
    try {
        if (!!environment) {
            const apiInstance = getApiInstance(environment);
            if (method === 'get') {
                response = await apiInstance.get(path, { params });
            }
            else if (method === 'post') {
                response = await apiInstance.post(path, payload, { params });
            }
        }
        else if (!!webApi) {
            if (method === 'get') {
                response = await webApi.get(path, { params });
            }
            else if (method === 'post') {
                response = await webApi.post(path, payload, { params });
            }
        }
        return response?.data || {};
    }
    catch (error) {
        if (onError) {
            onError(error);
        }
    }
};
export const getPIOversightList = async ({ environment, params, onError, }) => {
    return await makeApiCall({
        method: 'get',
        path: '/v2/routes/pi_oversight/list_participants',
        params,
        environment,
        onError,
    });
};
export const getPIOversightReviewBundle = async ({ environment, params, onError, }) => {
    return await makeApiCall({
        method: 'get',
        path: '/v2/routes/pi_oversight/get_review_bundle',
        params,
        environment,
        onError,
    });
};
export const getPIOversightSummary = async ({ webApi, payload, onError, }) => {
    return await makeApiCall({
        method: 'post',
        path: '/api/generative/pi_oversight/summary',
        payload,
        webApi,
        onError,
    });
};
export const getActivityTaskResponses = (responses) => {
    const activityResponses = [];
    const groupedResponsesByKey = responses.reduce((acc, response) => {
        const key = response.taskResponseId;
        acc[key] = [...(acc[key] || []), response];
        return acc;
    }, {});
    Object.values(groupedResponsesByKey).forEach((responses) => {
        activityResponses.push({
            activity: responses[0]?.activity ?? '',
            taskResponseCompletedDate: responses[0]?.taskResponseCompletedDate,
            taskResponseId: responses[0]?.taskResponseId ?? '',
            responses,
        });
    });
    return activityResponses.sort((a, b) => {
        const aTime = Date.parse(a.taskResponseCompletedDate || '') || 0;
        const bTime = Date.parse(b.taskResponseCompletedDate || '') || 0;
        return bTime - aTime;
    });
};
export const signOffPIOversight = async ({ environment, payload, onError, }) => {
    return await makeApiCall({
        method: 'post',
        path: '/v2/routes/pi_oversight/sign',
        payload,
        environment,
        onError,
    });
};
